/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { Box, Popover, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AchievementsBadgePopoverProps } from './AchievementsBadgePopover.props';
import { styled } from '@mui/material/styles';
import { handleErrorImg, parseImgURLBadge, roundUpPercentage, getBadgeDescription } from '../../utils/util';

// Styled components
const LeftIconBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#0d2b4c',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 100,
  height: '100%'
}));

const HighlightBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#EAF7C6',
  border: '1px solid #A2BC30',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1.5),
  whiteSpace: 'nowrap',
  display: 'inline-block',
  textAlign: 'center',
  minWidth: 170
}));

const ContentBox = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
}));

const AchievementsBadgePopover: React.FC<AchievementsBadgePopoverProps> = (props) => {
  const { 
    data, 
    anchorReference = "anchorEl", 
    anchorPosition, 
    anchorOrigin = { vertical: "top", horizontal: "center" },
    transformOrigin = { vertical: "bottom", horizontal: "center" }
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const roundUpProgress = roundUpPercentage(data?.peer_percentage ?? 0);
  const badgeDescription = getBadgeDescription(data?.badgeId ?? '', data?.status ?? '');
  return (
    <Popover
      open={props.open}
      anchorEl={props.anchorEl}
      anchorReference={anchorReference}
      anchorPosition={anchorPosition}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      // anchorReference="anchorPosition" // using anchorPosition
      // anchorPosition={{ top: 127, left: 266 }} // using fixed popover
      // anchorOrigin={{          // open when need to show Popover on icon (remove anchorPosition)
      //   vertical: "top",
      //   horizontal: "left",
      // }}
      // transformOrigin={{      // open when need to show Popover  on icon
      //   vertical: "bottom",
      //   horizontal: "right",
      // }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            p: 0,
            height: 150,
            maxWidth: 820,
            width: isMobile ? 'calc(100% - 32px)' : '100%',
            boxShadow: 3
          }
        }
      }}
      sx={{ pointerEvents: 'none' }}
      disableScrollLock={true}
      disableEnforceFocus // allow external focus
      disableAutoFocus // no focus force when open
      disableRestoreFocus // do not restore focus when closing
      TransitionProps={{
        onExited: props.onExited
      }}>
      <Stack direction="row" sx={{ width: '100%', height: '100%' }}>
        {/* Left Icon section */}
        <LeftIconBox>
          {data?.category && data?.badgeId ? (
            <img
              style={{ height: 76, width: 76 }}
              src={parseImgURLBadge(data?.category, data?.badgeId)}
              alt={`badge-${data?.name}`}
              onError={handleErrorImg}
            />
          ) : null}
        </LeftIconBox>

        {/* Right content */}
        <ContentBox>
          {/* old Stack ... replace with this */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto', // text | green box
              alignItems: 'center',
              columnGap: '10px' // EXACT 10px
            }}>
            <Box pb={2} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} fontSize="18px" lineHeight={1}>
                {data?.name}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                fontSize="16px"
                pt="12px"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                {badgeDescription}
              </Typography>
            </Box>

            <Box sx={{ mt: 1 }}>
              <HighlightBox>
                <Typography
                  variant="caption"
                  color="#000000"
                  fontWeight={500}
                  fontSize="14px"
                  sx={{ opacity: 0.9 }}>
                  {Math.min(100, roundUpProgress)}% have unlocked this.
                </Typography>
              </HighlightBox>
            </Box>
          </Box>
        </ContentBox>
      </Stack>
    </Popover>
  );
};

export default AchievementsBadgePopover;
